import { useEffect, useMemo, useRef, useState } from 'react'
import { HiCheck, HiChevronDown, HiMapPin } from 'react-icons/hi2'

type CitySelectorOption = {
  id: string
  name: string
}

type CitySelectorProps = {
  value: string
  options: CitySelectorOption[]
  onChange: (value: string) => void
  variant?: 'topbar' | 'mobile'
  className?: string
}

const stylesByVariant = {
  topbar: {
    wrapper: 'relative shrink-0',
    button:
      'group inline-flex items-center gap-1.5 text-gray-600 transition-colors hover:text-primary',
    iconBox:
      'relative inline-flex items-center justify-center text-current',
    label: 'block max-w-[140px] truncate text-xs font-medium tracking-[0.02em] text-current',
    value: 'hidden',
    chevron:
      'h-3 w-3 shrink-0 text-gray-400 transition-transform duration-200 group-data-[open=true]:rotate-180',
    menu:
      'absolute left-0 top-[calc(100%+8px)] z-40 min-w-[220px] overflow-hidden rounded-2xl border border-gray-200 bg-white p-2 shadow-[0_20px_40px_-24px_rgba(15,23,42,0.35)]',
    option:
      'flex w-full items-center justify-between gap-3 rounded-2xl px-4 py-3 text-left transition-colors hover:bg-gray-50',
    optionActive: 'bg-primary/8 text-gray-900',
    optionIdle: 'text-gray-700',
    optionTextWrap: 'min-w-0 flex-1',
    optionLabel: 'block text-sm font-semibold',
    optionHint: 'mt-0.5 block text-xs text-gray-500',
  },
  mobile: {
    wrapper: 'relative w-full',
    button:
      'group flex min-h-[58px] w-full items-center gap-3 rounded-2xl border border-gray-200 bg-white px-3 py-2.5 text-left transition-colors hover:border-gray-300',
    iconBox:
      'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-500 transition-colors group-hover:bg-primary/10 group-hover:text-primary',
    label: 'text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400',
    value: 'mt-0.5 block truncate text-sm font-semibold text-gray-900',
    chevron:
      'ml-auto h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 group-data-[open=true]:rotate-180',
    menu:
      'absolute left-0 right-0 top-[calc(100%+8px)] z-40 overflow-hidden rounded-2xl border border-gray-200 bg-white p-2 shadow-xl',
    option:
      'flex w-full items-center justify-between gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-gray-50',
    optionActive: 'bg-primary/8 text-gray-900',
    optionIdle: 'text-gray-700',
    optionTextWrap: 'min-w-0 flex-1',
    optionLabel: 'block text-sm font-semibold',
    optionHint: 'mt-0.5 block text-[11px] text-gray-500',
  },
} as const

const CitySelector = ({
  value,
  options,
  onChange,
  variant = 'topbar',
  className = '',
}: CitySelectorProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const styles = stylesByVariant[variant]

  const selectedOption = useMemo(
    () => options.find((option) => option.id === value) ?? options[0] ?? null,
    [options, value]
  )

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  if (!selectedOption) {
    return null
  }

  return (
    <div ref={rootRef} className={`${styles.wrapper} ${className}`.trim()}>
      <button
        type="button"
        data-open={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className={styles.button}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Выбор города"
      >
        <div className={styles.iconBox}>
          <HiMapPin className={variant === 'topbar' ? 'h-3.5 w-3.5' : 'h-4.5 w-4.5'} />
        </div>

        {variant === 'topbar' ? (
          <>
            <span className={styles.label}>{selectedOption.name}</span>
            <HiChevronDown className={styles.chevron} />
          </>
        ) : (
          <div className="min-w-0 flex-1">
            <span className={styles.label}>Город</span>
            <span className={styles.value}>{selectedOption.name}</span>
          </div>
        )}
      </button>

      {isOpen ? (
        <div className={styles.menu} role="listbox" aria-label="Список городов">
          {options.map((option) => {
            const isSelected = option.id === selectedOption.id

            return (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(option.id)
                  setIsOpen(false)
                }}
                className={`${styles.option} ${
                  isSelected ? styles.optionActive : styles.optionIdle
                }`}
              >
                <span className={styles.optionTextWrap}>
                  <span className={styles.optionLabel}>{option.name}</span>
                  <span className={styles.optionHint}>
                    {isSelected && 'Выбран текущий город'}
                  </span>
                </span>

                {isSelected ? <HiCheck className="h-4 w-4 shrink-0 text-primary" /> : null}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

export default CitySelector
